<i18n>
{
    "en": {},
    "ru": {
        "add_new_perfume": "Добавьте ароматы на свою полку",
        "shelf_is_empty": "На этой полке пока нет ароматов",
        "add_new_perfume_favorites": "Добавьте свои любимые ароматы",
        "add_perfume": "Добавить аромат",
        "add_perfumes": "Добавить аромат",
        "change_perfumes": "Изменить ароматы",
        "set_shelf_main": "Сделать полку главной",
        "rename_shelf": "Переименовать полку",
        "hide_shelf": "Скрыть полку",
        "set_shelf_visible": "Сделать полку видимой",
        "remove_shelf": "Удалить полку",
        "remove_shelf_description": "Вы уверены, что хотите удалить полку <span class='bold'>“{shelf_name}”</span> со всеми ароматами? Это действие невозможно будет отменить.",
        "open_shelf": "Открыть полку",
        "share_shelf": "Поделиться полкой",
        "share_title": "{fullname} | {shelf_name} – Гардероб на Aromo.ru"
    }
}
</i18n>

<template>
    <div
        class="shelf"
        :class="shelfClasses">
        <header class="shelf__header"
            v-if="!isGrid">
            <div
                @touchend="onEndTouch"
                @touchstart="onStartTouch"
                v-if="draggable"
                class="shelf-draggable-btn">
                <svg-icon name="icon-drag"/>
            </div>
            <h5 class="shelf-title">
                <nuxt-link :to="wardrobeLink">{{ title }}</nuxt-link>
            </h5>
            <span v-if="itemsTotal > 0" class="shelf-count">{{ itemsTotal }}</span>
            <svg-icon
                v-if="isMain"
                class="shelf-icon"
                name="icon-home"/>
            <svg-icon
                v-if="isHidden"
                class="shelf-icon"
                name="icon-crossed-eye"/>

            <div
                @click="showActions = true"
                v-click-outside="hideActions"
                v-if="isDeviceDesktop && actionsEnable"
                class="shelf-actions">
                <ButtonCebab v-if="!draggable" class="shelf-actions shelf-actions__button"/>
                <!-- @click.native="onMoreClick(title)" -->
                <transition name="fade">
                    <Actions
                        is-tooltip
                        v-show="showActions"
                        :modalItems="actionModelItems"/>
                </transition>
            </div>

            <ButtonCebab
                v-else-if="actionsEnable"
                class="shelf-actions"
                @click.native="onMoreClick(title)"/>
        </header>

        <template v-if="itemsTotal > 0">

            <div class="shelf-list" v-if="isGrid">
                <ScrollLoader class="shelf-list__wrapper"
                    @onLoad="onShelfScroll">
                    <div
                        v-for="(item, index) in sortedItems"
                        :key="`shelf-perfume-${get(item, 'id')}`"
                        class="shelf-list__item"
                        v-tooltip="tooltipOptions(getTooltipName(item))">

                        <div v-if="draggable" class="shelf-list__overlay"></div>

                        <img
                            @click="openPerfume(item.perfume)"
                            class="shelf-list__image"
                            v-lazy-load :data-src="get(item.perfume, 'picture.url', getPerfumeNoImage)"
                            :alt="get(item.perfume, 'picture.alt', '')"/>

                        <div class="shelf__bottom" v-if="(isGrid && showShelfPattern(index))">
                            <svg-icon name="shelf-leg" class="shelf__bottom-leg shelf__bottom-leg_left"/>
                            <svg-icon name="shelf-leg" class="shelf__bottom-leg shelf__bottom-leg_right"/>
                        </div>
                    </div>
                </ScrollLoader>
            </div>

            <div v-else
                class="shelf-list"
                :class="draggable ? 'shelf-list_draggable' : ''">
                <ul @scroll="onScroll" class="shelf-list__wrapper shelf-list__wrapper_scroll">
                    <li
                        v-for="item in sortedItems"
                        :key="`shelf-perfume-${get(item, 'id')}`"
                        class="shelf-list__item"
                        v-tooltip="tooltipOptions(getTooltipName(item))">

                        <div v-if="draggable" class="shelf-list__overlay"></div>

                        <img
                            @click="openPerfume(item.perfume)"
                            class="shelf-list__image"
                            v-lazy-load :data-src="get(item.perfume, 'picture.url', getPerfumeNoImage)"
                            :alt="get(item.perfume, 'picture.alt', '')"/>
                    </li>
                </ul>
            </div>

        </template>

        <div v-else-if="isCurrentProfile" class="shelf__empty">
            <p class="shelf__empty-title">
                {{ favoritesShelfTitle(title) }}
            </p>
            <span class="shelf__empty-link"
                :class="emptyLinkClasses"
                @click="onClickAddPerfume">
                {{ $t('add_perfumes') }}
            </span>
        </div>

        <div v-else class="shelf__empty shelf__empty_stranger">
            <p class="shelf__empty-title">
                {{ $t('shelf_is_empty') }}
            </p>
        </div>
        <div class="shelf__bottom" v-if="!isGrid || itemsTotal === 0" >
            <svg-icon name="shelf-leg" class="shelf__bottom-leg shelf__bottom-leg_left"/>
            <svg-icon name="shelf-leg" class="shelf__bottom-leg shelf__bottom-leg_right"/>
        </div>
    </div>
</template>

<script>
import ClickOutside from "vue-click-outside";

import { mapGetters } from "vuex"
import get from 'lodash/get'

import wardrobe from "@/mixins/profile/wardrobe";

import ButtonCebab from '@/components/ui/buttons/ButtonCebab'
import Actions from '@/components/modal/ui/Actions'
import Perfume from "@/models/catalog/perfume";
import Perfumer from "@/models/catalog/Perfumer";

import ScrollLoader from '@/components/ui/ScrollLoader'

export default {
    name: 'WardrobeShelf',
    directives: { ClickOutside },
    components: {
        ButtonCebab,
        Actions,
        ScrollLoader
    },
    mixins: [wardrobe],
    props: {
        title: {
            type: String,
            default: 'Пустая полка'
        },
        shelves: {
            type: Array,
            default: () => []
        },
        perfumes: {
            type: Array,
            default: () => []
        },
        isMain: {
            type: Boolean,
            default: false
        },
        isHidden: {
            type: Boolean,
            default: false
        },
        draggable: {
            type: Boolean,
            default: false
        },
        shelfId: {
            type: Number,
            default: null
        },
        isDefault: {
            type: Boolean,
            default: false
        },
        defaultCode: {
            type: String,
            default: ''
        },
        actionsEnable: {
            type: Boolean,
            default: false
        },
        total: {
            type: [String, Number],
            default: null
        },
        isGrid: {
            type: Boolean,
            default: false
        },
        userFullname: {
            type: String,
            default: null
        },
        perPage: {
            type: Number,
            default: 10
        }
    },
    data: () => ({
        showActions: false,
        loading: false,
        sortedItems: [],
        page: 2,
        loadPerfumeData: false
    }),
    computed: {
        ...mapGetters(['isDeviceDesktop', 'getPerfumeNoImage']),
        shelfClasses() {
            if(this.itemsTotal === 0 && this.draggable) {
                return 'shelf_draggable shelf_blank'
            } else if (this.draggable) {
                return 'shelf_draggable'
            } else if (this.isGrid) {
                return 'shelf_grid'
            } else {
                return ''
            }
        },
        emptyLinkClasses() {
            return {
                'shelf__empty-link_disabled': this.draggable
            }
        },
        itemsTotal() {
            return this.total || get(this, 'perfumes.length', 0)
        },
        isFavorite() {
            return this.defaultCode === 'favorite'
        },
        wardrobeLink(){
            return {
                name: 'WardrobeDetail',
                params: {
                    userId: get(this.$route, 'params.userId', undefined),
                    shelfId: this.shelfId
                }
            }
        }
    },
    watch: {
        perfumes(value) {
            this.sortedItems = value
        }
    },
    mounted() {
        this.sortedItems = this.perfumes
    },
    methods: {
        get,
        onStartTouch() {
            this.$emit('touchstart')
        },
        onEndTouch() {
            this.$emit('touchend')
        },
        hideActions() {
            this.showActions = false;
        },
        getTooltipName(item) {
            return get(item, 'perfume.brand.name', '') + ' ' +get(item, 'perfume.name', '')
        },
        tooltipOptions(name) {
            const desktopOptions = {
                content: name,
                delay: { show: 50, hide: 50 },
                offset: 8,
                defaultPlacement: 'bottom',
                boundariesElement: 'body'
            }
            return this.isDeviceDesktop ? desktopOptions : {}
        },
        onClickAddPerfume() {
            if(this.draggable) return;

            if(this.isDeviceDesktop) {
                this.showPopup('modal-wardrobe-add-perfume', {
                    modalType: 'add',
                    currentShelf: {
                        id: this.shelfId
                    },
                    excludedPerfumes: this.perfumes
                });
            } else {
                const link = {
                    name: 'WardrobeAddPerfume',
                    params: {
                        userId: get(this.$auth, 'user.id'),
                        shelfId: this.shelfId
                    }
                };

                this.$router.push(link)
            }
        },
        onScroll(e) {
            if (this.loading) {
                this.faded = false;
                return false;
            }

            if (e.currentTarget.scrollLeft + e.currentTarget.clientWidth >= e.currentTarget.scrollWidth - 50) {
                this.loadShelfNextPage(this.shelfId);
            }

            document.querySelectorAll('.tooltip').forEach(elem => {elem.style.display = 'none'})
        },
        onMoreClick(shelfName) {
            const modalItems = this.actionModelItems;

            this.showPopup('actions', {
                modalItems,
            });
        },
        async openPerfume(item) {
            if(this.loadPerfumeData) return;
            if(item && item.code) {
                this.loadPerfumeData = true;

                const perfumeDetailData = await new Perfume().find(item.code);
                const perfumersResponse = await new Perfumer()
                    .for((new Perfume({code: item.code}))).get();
                const perfumers = get(perfumersResponse, '[0]', null)

                this.showPopup('card-perfume', {
                    cardData: item,
                    perfumeDetailData,
                    perfumers
                })

                this.loadPerfumeData = false;
            }
        },
        favoritesShelfTitle() {
            if(this.defaultCode === 'favorite') {
                return this.$t('add_new_perfume_favorites')
            } else {
                return this.$t('add_new_perfume')
            }
        },
        showShelfPattern(index){
            if(this.isDeviceDesktop)
                return index % 7 === 0

            if(index+1 === this.sortedItems.length)
                return true
            
            return index % 2 === 0
        },
        onShelfScroll(){
            if (this.loading) {
                this.faded = false;
                return false;
            }
            this.loadShelfNextPage(this.shelfId)
        }
    }
}
</script>

<style>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */ {
    opacity: 0;
}
</style>

<style lang="less">

.sortable-drag {
    border-radius: rem(3);

    padding-top: rem(14);
    padding-bottom: rem(28);

    .shelf__bottom {
        bottom: rem(20);
    }

    .shelf-list {
        &::before, &::after {
            @{desktop} & {
                height: rem(68);
            }
        }
    }
}

.shelf {
    background-color: @color-white;

    height: rem(146);

    position: relative;

    &_detail{
        height: auto;
    }

    @{desktop} & {
        margin-right: rem(-8);
        margin-left: rem(-8);
        padding-right: rem(8);
        padding-left: rem(8);
    }

    &_ghost {
        opacity: 0;
    }

    &__bottom {
        background-color: @color-gray-white;
        border-radius: rem(1);
        height: rem(6);
        width: calc(100% - rem(24));
        position: absolute;
        bottom: rem(33);
        left: rem(12);
        pointer-events: none;

        @{desktop} & {
            width: rem(616);

            left: rem(8);
        }

        &-leg {
            height: rem(14);
            width: rem(13);

            position: absolute;

            &_left {
                top: 100%;
                left: rem(32);
            }

            &_right {
                top: 100%;
                right: rem(32);
            }
        }
    }

    &__header {
        display: flex;
        align-items: center;

        .page-padding();
    }

    &__empty {
        text-align: center;

        height: rem(22);

        .page-padding();

        &_stranger {
            margin-top: rem(30);
        }

        &-title {
            color: @color-gray-light;

            margin: rem(16) 0 rem(5);
        }

        &-link {
            color: @color-violet;

            .hover({
                cursor: pointer;
                text-decoration: underline;
            });

            &_disabled {
                color: @color-gray-light;

                .hover({
                    cursor: default;
                });
            }
        }
    }

    &_draggable {
        &::after {
            opacity: 0.3;
        }

        .shelf-title, .shelf-count, .shelf-list, .shelf-actions, .shelf__empty {
            pointer-events: none;
            user-select: none;
        }
    }

    &_grid{

        .shelf-list{

            &::before,
            &::after{
                display: none;
            }

            &__wrapper{
                padding: 0;
                white-space: normal;
                display: flex;
                flex-wrap: wrap;
                overflow: visible;

                @{mobile} & {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, rem(64));
                    grid-gap: rem(52) rem(13);
                    justify-content: space-around;
                    padding: 0 rem(20);
                }
            }

            &__item{
                margin: 0 rem(14) rem(40);
                position: static;

                .shelf__bottom{
                    bottom: auto;
                    left: 0;
                    margin-top: rem(12);

                    @{mobile} & {
                        left: rem(12);
                    }
                }

                @{desktop} & {
                    &:last-child,
                    &:nth-child(7n+7){
                        margin-right: 0;
                    }

                    &:first-child,
                    &:nth-child(7n+1){
                        margin-left: 0;
                    }
                }

                @{mobile} & {
                    // margin: 0 rem(13) rem(52);
                    margin: 0;
                }
            }
        }

    }
}

.shelf-list {
    position: relative;

    &::before {
        .gradient(left);

        height: 76%;

        left: rem(-2);
        z-index: 2;

        @{desktop} & {
            height: rem(78);
        }
    }

    &::after {
        .gradient(right);

        height: 76%;

        right: rem(-1);
        z-index: 2;

        @{desktop} & {
            height: rem(78);
        }

        @{mobile} & {
            right: 0;
        }
    }

    &__wrapper {
        list-style: none;
        white-space: nowrap;

        margin: 0;
        padding: rem(12) @dim-page-padding rem(10);

        min-height: rem(100);
        max-width: 100%;
        
        &_scroll{
            .scrollbarHorizontal();
        }
    }

    &__item {
        display: inline-block;
        vertical-align: top;
        cursor: pointer;
        text-align: center;
        margin-right: rem(16);
        height: rem(64);
        width: rem(64);
        position: relative;
    }

    &__picture {
        max-height: 100%;
        max-width: 100%;
    }

    &__overlay {
        background-color: @color-white;

        height: 100%;
        width: 100%;

        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;

        opacity: 0.3;
    }

    &__image {
        height: 100%;
        max-width: 100%;
    }

    &_draggable {
        pointer-events: none;
        user-select: none;
    }
}

.shelf-title {
    font-size: rem(16);
    font-weight: 500;
    line-height: rem(22);

    margin: 0;

    &>a{
        color: @color-black;

        &:hover{
            color: @color-primary;
        }
    }
}

.shelf-icon {
    stroke: @color-intense-blue;

    margin-left: rem(6);

    height: rem(15);
    width: rem(15);
    position: relative;
    top: rem(-2);
}

.shelf-count {
    display: block;

    color: @color-gray-light;
    font-size: rem(13);
    font-weight: 500;
    line-height: 1;

    margin: 0 0 0 rem(7);
}

.shelf-actions {
    margin-left: auto;

    position: relative;
}

.shelf-draggable-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;

    margin-top: rem(-2);
    margin-right: rem(10);

    height: rem(20);
    width: rem(20);
}

</style>
